import React from 'react';
import { motion } from 'motion/react';
import { CalendarDays, Clock, MapPin, Users } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';

const BALLOONS = ['🎈', '🎉', '🎊', '🎂', '🥳', '🎁', '✨', '🌟'];

function FloatingBalloon({ emoji, x, delay }: { emoji: string; x: string; delay: number }) {
  return (
    <motion.span
      className="pointer-events-none absolute text-2xl select-none"
      style={{ left: x, bottom: '-2rem' }}
      animate={{ y: [0, -600], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 6, delay, repeat: Infinity, ease: 'easeInOut' }}
    >
      {emoji}
    </motion.span>
  );
}

export function HeroSection({ content }: { content: BirthdayContent['hero'] }) {
  const hasBackground = Boolean(content.backgroundImageUrl);

  return (
    <section className="relative min-h-screen overflow-hidden flex flex-col items-center justify-center px-6 py-20 text-center">
      {/* Background image with blur + dark overlay */}
      {hasBackground ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${content.backgroundImageUrl})`,
              filter: 'blur(18px)',
              transform: 'scale(1.08)',
            }}
          />
          <div className="absolute inset-0 bg-black/55" />
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 40%, #1d4ed8 100%)' }}
        />
      )}

      {/* floating balloons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {BALLOONS.map((emoji, i) => (
          <FloatingBalloon key={i} emoji={emoji} x={`${i * 12.5 + 2}%`} delay={i * 0.75} />
        ))}
      </div>

      {/* decorative rings */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        {[400, 600, 800].map((size, i) => (
          <motion.div
            key={size}
            className="absolute rounded-full border border-blue-400/10"
            style={{ width: size, height: size, top: -size / 2, left: -size / 2 }}
            animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.15, 0.3] }}
            transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.8 }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center gap-8">
        {/* badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: BDAY_EASE }}
          className="inline-flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 text-blue-200 text-xs font-bold uppercase tracking-widest px-5 py-2.5 rounded-full"
        >
          🎂 Join Us to Celebrate
        </motion.div>

        {/* main photo — circular portrait (no blur, sharp face) */}
        {content.backgroundImageUrl && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: BDAY_EASE }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-full bg-blue-400/30 blur-2xl scale-110" />
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              className="relative w-40 h-40 rounded-full border-4 border-blue-400/60 overflow-hidden shadow-2xl shadow-blue-900/60"
            >
              <img
                src={content.backgroundImageUrl}
                alt={content.birthdayPersonName}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>
        )}

        {/* name + age */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: BDAY_EASE }}
        >
          <p className="text-blue-300 text-sm font-medium uppercase tracking-[0.2em] mb-2">
            Celebrating
          </p>
          <h1 className="text-5xl sm:text-7xl font-extrabold text-white leading-none tracking-tight">
            {content.birthdayPersonName}
          </h1>
          {content.ageTurning && (
            <p className="mt-2 text-blue-200 text-2xl font-light">
              Turning <span className="font-bold text-blue-300">{content.ageTurning}</span>
            </p>
          )}
          <p className="mt-3 text-blue-200 text-lg font-light italic">
            {content.tagline}
          </p>
        </motion.div>

        {/* meta chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5, ease: BDAY_EASE }}
          className="flex flex-wrap items-center justify-center gap-3"
        >
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2.5 text-white text-sm">
            <CalendarDays size={15} className="text-blue-300" />
            {content.eventDate}
          </div>
          {content.eventTime && (
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2.5 text-white text-sm">
              <Clock size={15} className="text-blue-300" />
              {content.eventTime}
            </div>
          )}
          {content.venueName && (
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2.5 text-white text-sm">
              <MapPin size={15} className="text-blue-300" />
              {content.venueName}
            </div>
          )}
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2.5 text-white text-sm">
            <Users size={15} className="text-blue-300" />
            Hosted by {content.hostNames}
          </div>
        </motion.div>

        {/* scroll cue */}
        <motion.div
          animate={{ y: [0, 8, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="mt-4 text-blue-300 text-xs tracking-widest uppercase"
        >
          ↓ Scroll to explore
        </motion.div>
      </div>
    </section>
  );
}
